// Phase 6 Part 1 (10/06) — pure view-model for the "Yesterday" portfolio digest
// card. Turns the /api/reports/portfolio-digest payload into exactly one of:
//   { mode: 'empty', title, detail, link }   — nothing to show, and WHY
//   { mode: 'data',  day, stale, paused, headline[], active[], byStatus[], movers[], funded[] }
// The hard rule: figures appear ONLY when a report document exists. Every other
// case is an explicit empty state; a silent $0.00 on a money tile is a bug.

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : parseFloat(v) || 0);

export const JOBS_LINK = '/admin/jobs';

// Mirrors jobs/daily/_common.ACTIVE_STATUSES. The by-status table on this
// operations card shows ACTIVE buckets only (Josh 10/06): terminal buckets
// (Paid in Full, Paid in Full / Settled, Collected, Default) carry permanent
// negatives from over-collected settled deals and display-zeroed balances,
// which reads as a data error here. View-model filter only — the job's stored
// balances_by_status is untouched and still feeds the digest e-mail.
export const ACTIVE_STATUSES = ['Funded', 'Current', 'Active', 'Payment Issues'];

export const formatDay = (iso) => {
  if (!iso) return '';
  // Noon avoids the UTC-midnight shift that would print the previous day.
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

export const buildDigestView = (payload, { pinnedDate = null } = {}) => {
  if (!payload) {
    return { mode: 'empty', title: 'Digest unavailable', detail: 'The server did not return a digest.', link: null };
  }
  const { state, report, enabled, last_run: lastRun, reason, yesterday, expected_date: expectedDate, next_run_at: nextRunAt } = payload;

  // 10/08 (Josh): a past day was picked explicitly. A missing doc for that day is
  // "no digest for that date", not a job problem; and a past doc is never "stale".
  if (pinnedDate && !report) {
    return {
      mode: 'empty',
      title: `No digest for ${formatDay(pinnedDate)}`,
      detail: 'portfolio_digest did not store a snapshot for that day (it only keeps days it ran for).',
      link: JOBS_LINK,
      pinned: true,
    };
  }

  if (!report) {
    if (state === 'disabled') {
      return {
        mode: 'empty',
        title: 'Digest paused',
        detail: 'portfolio_digest is disabled, so there is no snapshot to show. Enable it under Admin → Jobs.',
        link: JOBS_LINK,
      };
    }
    if (state === 'error') {
      const err = lastRun && lastRun.error ? ` Last error: ${lastRun.error}` : '';
      return {
        mode: 'empty',
        title: 'Digest failed',
        detail: `portfolio_digest errored on its last run and left no snapshot.${err}`,
        link: JOBS_LINK,
      };
    }
    return {
      mode: 'empty',
      title: 'No digest yet',
      detail: reason || "No digest yet — portfolio_digest hasn't run. Run it from Admin → Jobs.",
      link: JOBS_LINK,
    };
  }

  const data = report.data || {};
  const c = data.counts || {};
  const day = data.day || report.date;
  // 10/08 (Josh): today is computed live from the ledger — no snapshot, figures move.
  const live = state === 'live' || report.live === true;
  const stale = !pinnedDate && !live && state === 'stale';
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  const headline = [
    { key: 'collected', label: 'Collected', value: num(c.collected), kind: 'money',
      sub: plural(num(c.payments), 'payment') },
    { key: 'bounced', label: 'Bounced', value: num(c.bounced), kind: 'money',
      sub: plural(num(c.bounces), 'bounce'), warn: num(c.bounces) > 0 },
    { key: 'fees', label: 'Fees', value: num(c.fees), kind: 'money', sub: 'bounce fees' },
    { key: 'funded', label: 'Funded', value: num(c.funded_amount), kind: 'money',
      sub: plural(num(c.funded_deals), 'deal') },
  ];
  const active = [
    { key: 'active_deals', label: 'Active Deals', value: num(c.active_deals), kind: 'count' },
    { key: 'active_balance', label: 'Active Balance', value: num(c.active_balance), kind: 'money' },
  ];
  const byStatus = Object.entries(data.balances_by_status || {})
    .filter(([status]) => ACTIVE_STATUSES.includes(status))
    .map(([status, v]) => ({ status, deals: num(v && v.deals), balance: num(v && v.balance) }))
    .sort((a, b) => b.balance - a.balance || b.deals - a.deals);
  const movers = (data.top_movers || []).map((m) => ({
    deal: m.deal || m.deal_id, dealId: m.deal_id, net: num(m.net),
  }));
  const funded = (data.funded_yesterday || []).map((f) => ({
    deal: f.deal || f.deal_id, dealId: f.deal_id, amount: num(f.funding_amount),
  }));

  return {
    mode: 'data',
    day,
    dayLabel: formatDay(day),
    // The newest date the schedule could have produced by now (server-computed);
    // before the morning run that is two days back, so no false "stale".
    expectedDay: expectedDate || yesterday,
    // True when the doc is current but not yet "yesterday": the run is pending.
    awaitingRun: !pinnedDate && !live && !stale && Boolean(yesterday) && day < yesterday,
    pinned: Boolean(pinnedDate),
    live,
    computedAt: live ? (report.computed_at || null) : null,
    nextRunAt: nextRunAt || null,
    stale,
    paused: enabled === false,
    headline,
    active,
    byStatus,
    movers,
    funded,
  };
};
